import React, { useState, useEffect, useRef } from "react";
import _ from "lodash";
import AppConstants from "../../constants/AppConstants";
import AppConfig from "../../config/app";
import { getSiteId } from "../../hooks/query/useSite";
import SiteActions from "../../actions/SiteActions";
import UIActions from "../../actions/UIActions";
import DesignManager from "../../managers/DesignManager";
import DesignUtils from "../../utils/DesignUtils";
import SubviewProcessing from "../SubviewProcessing";
import LibrarySiteChooserThumbnail from "../LibrarySiteChooserThumbnail";
import UpgradedIcon from "../UpgradedIcon";
import { Text } from "@showitinc/showit-wc";
import UserSiteChooserMenu from "./UserSiteChooserMenu";
import UserSiteChooserHeader from "./UserSiteChooserHeader";
import useSites, { invalidateUserSites } from "../../hooks/query/useSites";
import useSiteDesignsFolders, {
  invalidateSiteDesignsFolders,
} from "../../hooks/query/useSiteDesignsFolders";
import { queryClient } from "../../utils/QueryUtils";
import SearchUtils from "../../utils/SearchUtils";
import { USER_SITES, SITE_DESIGNS_FOLDERS } from "../../constants/QueryKeys";
import styles from "./UserSiteChooserView.module.css";
import { handleSelection } from "../../utils/SelectionUtils";
import CreateFolderModal from "./CreateFolderModal";
import DeleteFolderModal from "./DeleteFolderModal";
import MoveSitesModal from "./MoveSitesModal";
import UserActions from "../../actions/UserActions";
import SiteFolderConstants from "@/constants/SiteFolderDefaultConstants";
import UserService from "../../service/UserService";

const { CREATE_MODE, RENAME_MODE } = SiteFolderConstants;

const {
  USER_SITES_VIEW_MODE_SELECT,
  USER_SITES_VIEW_MODE_MANAGE,
  USER_SITES_VIEW_LIST,
  USER_SITES_VIEW_CREATING,
  LAYOUT_DESKTOP,
  MAIN_VIEW_LIBRARY_SITE_CHOOSER,
} = AppConstants;

const UserSiteChooserView = ({ closeEnabled = false, mode = USER_SITES_VIEW_MODE_SELECT }) => {
  const assignmentModeRef = useRef(false);
  const selectedSitesRef = useRef([]);

  const [state, setState] = useState({
    selectedSite: null,
    selectedSites: [],
    lastSelectedIndex: null,
    siteQuery: "",
    siteSort: "last_access",
    closeEnabled,
    mode,
    view: USER_SITES_VIEW_LIST,
    isAddFolderModalOpen: false,
    isRenameFolderModalOpen: false,
    isDeleteFolderModalOpen: false,
    isMoveSitesModalOpen: false,
    selectedFolder: "all-site-designs",
    folderBeingEdited: null,
    sitesInFolder: [],
    isFolderEmpty: false,
    isAssignmentMode: false,
    isLoadingSite: false,
  });

  const { folders: allFolders = [] } = useSiteDesignsFolders();

  const personalFolders = allFolders.filter((folder) => folder.name !== "Uncategorized");

  const {
    sites = [],
    isLoading,
    isSuccess,
  } = useSites({
    selectedFolder: state.selectedFolder,
    folders: allFolders,
  });

  // Get all sites (not filtered by current folder) for checking folder contents during deletion
  const { sites: allSites = [] } = useSites();

  const hasUpgradedSite = allSites.some(
    (site) => site?.renderer_toggle_enabled === 0,
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies intentionally omitted
  useEffect(() => {
    const unsubscribeCreate = UserActions.createSiteDesignsFolder.complete.listen(
      async (response) => {
        await invalidateSiteDesignsFolders();
        await queryClient.refetchQueries({ queryKey: [SITE_DESIGNS_FOLDERS] });

        if (
          assignmentModeRef.current &&
          selectedSitesRef.current.length > 0 &&
          response?.folderId
        ) {
          const siteIds = selectedSitesRef.current.map((site) => site.site_design_id);
          const folderIds = response.folderId;
          _handleMoveSites({ siteIds, folderIds });
          _handleNavigateToFolder(response.folderId);
        }
      },
    );

    const unsubscribeUpdate = UserActions.updateSiteDesignsFolder.complete.listen(() => {
      invalidateSiteDesignsFolders();
    });

    const unsubscribeDelete = UserActions.deleteSiteDesignsFolder.complete.listen(() => {
      invalidateSiteDesignsFolders();
    });

    const unsubscribeAssign = UserActions.assignSitesToFolder.complete.listen(() => {
      invalidateUserSites();
    });

    return () => {
      unsubscribeCreate?.();
      unsubscribeDelete?.();
      unsubscribeUpdate?.();
      unsubscribeAssign?.();
    };
  }, []);

  useEffect(() => {
    if (personalFolders.length === 0 && state.selectedFolder !== "all-site-designs") {
      setState((prev) => ({
        ...prev,
        selectedFolder: "all-site-designs",
        selectedSites: [],
        selectedSite: null,
        lastSelectedIndex: null,
      }));
    }
  }, [personalFolders.length, state.selectedFolder]);

  const activeSiteId = getSiteId();

  const _transformFoldersForNavigation = () => {
    const personalFoldersOnly = allFolders.filter((folder) => folder.name !== "Uncategorized");
    return personalFoldersOnly.map((folder) => ({
      value: folder.folderId,
      label: folder.name,
      iconType: "folderClosed",
    }));
  };

  /**
   * Builds the complete list of folder options including default options
   * @returns {Array} Complete list of folder options
   */
  const _buildCompleteFolderOptions = () => {
    const { ALL_SITE_DESIGNS, UNCATEGORIZED, ADD_NEW_FOLDER } = SiteFolderConstants;
    const customFolders = _transformFoldersForNavigation();

    return [ALL_SITE_DESIGNS, ...customFolders, UNCATEGORIZED, ADD_NEW_FOLDER];
  };

  /**
   * Handles folder selection from the dropdown
   * Intercepts "Add New Folder" selection to open modal instead
   * @param {Event} e - The select event
   */
  const _onFolderSelectFromDropdown = (e) => {
    const selectedValue = e.detail.value;

    if (selectedValue === SiteFolderConstants.ADD_NEW_FOLDER_ID) {
      _openAddFolderModal();
      return;
    }

    const allOptions = _buildCompleteFolderOptions();
    // biome-ignore lint/suspicious/noDoubleEquals: comparing mixed types (string/number) for folder ID matching
    const selectedOption = allOptions.find((option) => option.value == selectedValue);

    if (selectedOption) {
      _onFolderSelect(selectedOption);
    }
  };

  const _transformFoldersForManager = (selectedSites = []) => {
    const foldersToShow =
      personalFolders.length > 0
        ? allFolders
        : allFolders.filter((folder) => folder.name !== "Uncategorized");

    return foldersToShow.map((folder) => {
      const hasAssigned = selectedSites.some((site) => {
        if (folder.name === "Uncategorized") {
          return !site.folder_id || !allFolders.find((f) => f.folderId === site.folder_id);
        }
        return site.folder_id === folder.folderId;
      });
      return {
        value: folder.folderId,
        label: folder.name,
        iconType: "folderClosed",
        hasAssigned,
      };
    });
  };

  const _loadSite = (site) => {
    if (!AppConfig.demoMode && DesignManager.needsToSave()) {
      alert(
        "Current site design is still saving.  Please wait until save is complete before loading another design",
      );
    } else {
      setState((prev) => ({ ...prev, isLoadingSite: true }));
      SiteActions.loadSite(site);
      queryClient.setQueryData([USER_SITES], (prev) => ({
        ...prev,
        sites: prev.sites.map((cachedSite) => {
          if (cachedSite.site_design_id === site.site_design_id) {
            cachedSite.last_access = new Date().toISOString();
          }
          return cachedSite;
        }),
      }));
    }
  };

  const _selectSite = (site) => {
    if (_.isEmpty(site)) {
      site = null;
    }

    setState((prev) => ({ ...prev, selectedSite: site }));
  };

  const _toggleSiteSelection = (selection) => {
    const { site, index, rangeSelect, multiSelect, event } = selection;

    setState((prev) => {
      const filteredSites = _filterSites();

      const result = handleSelection({
        items: filteredSites,
        selection: { site, index, rangeSelect, multiSelect, event },
        currentSelected: prev.selectedSites.filter((s) => s),
        getItemId: (item) => item.site_design_id,
        lastSelectedIndex: prev.lastSelectedIndex,
      });

      const newState = {
        ...prev,
        selectedSites: result.newSelected.filter((s) => s),
        lastSelectedIndex: result.newLastIndex,
        selectedSite:
          result.newSelected.length > 0 ? result.newSelected[result.newSelected.length - 1] : null,
      };
      selectedSitesRef.current = newState.selectedSites;

      return newState;
    });
  };

  const _close = () => {
    UIActions.showPreviousView();
  };

  const _closeMenu = () => {
    setState((prev) => ({ ...prev, selectedSite: null }));
  };

  const _onSiteQuery = (e) => {
    setState((prev) => ({ ...prev, siteQuery: e.target.value }));
  };

  const _onSiteSort = (e) => setState((prev) => ({ ...prev, siteSort: e.target.value }));

  const _clearFilters = () =>
    setState((prev) => ({ ...prev, siteQuery: "", siteSort: "last_access" }));

  const _createNewSite = () => {
    const folderId = typeof state.selectedFolder === "number" ? state.selectedFolder : undefined;
    UIActions.showAppView({
      view: MAIN_VIEW_LIBRARY_SITE_CHOOSER,
      allowBack: true,
      props: { folderId },
    });
  };

  /**
   * Filters the sites by the search query and sorting
   * @returns {Array} The filtered sites
   */
  const _filterSites = () => {
    return SearchUtils.searchSort(sites, state.siteQuery, state.siteSort);
  };

  const _getCreatingSiteView = () => {
    const message = "Your Site Is Being Prepared";
    return <SubviewProcessing message={message} />;
  };

  const setLoadingState = () => {
    setState((prev) => ({ ...prev, view: USER_SITES_VIEW_CREATING }));
  };

  /**
   * Opens the add folder modal
   * @returns {void}
   */
  const _openAddFolderModal = (isFromAssignment = false) => {
    assignmentModeRef.current = isFromAssignment;
    setState((prev) => ({
      ...prev,
      isAddFolderModalOpen: true,
      isAssignmentMode: isFromAssignment,
    }));
  };

  const _closeAddFolderModal = () => {
    setState((prev) => ({
      ...prev,
      isAddFolderModalOpen: false,
      isAssignmentMode: false,
    }));
  };

  const _openRenameFolderModal = (folder) => {
    setState((prev) => ({
      ...prev,
      isRenameFolderModalOpen: true,
      folderBeingEdited: folder,
    }));
  };

  const _closeRenameFolderModal = () => {
    setState((prev) => ({
      ...prev,
      isRenameFolderModalOpen: false,
      folderBeingEdited: null,
    }));
  };

  const _openDeleteFolderModal = (folder) => {
    setState((prev) => ({
      ...prev,
      isDeleteFolderModalOpen: true,
      folderBeingEdited: folder,
      isFolderEmpty: true,
    }));
  };

  const _closeDeleteFolderModal = () => {
    setState((prev) => ({
      ...prev,
      isDeleteFolderModalOpen: false,
      folderBeingEdited: null,
      isFolderEmpty: false,
    }));
  };

  /**
   * Closes the move sites modal and resets the state
   * @returns {void}
   */
  const _closeMoveSitesModal = () => {
    setState((prev) => ({
      ...prev,
      isMoveSitesModalOpen: false,
      folderBeingEdited: null,
      sitesInFolder: [],
    }));
  };

  /**
   * Handles moving sites from a folder and then deleting the folder
   * @param {string|number} targetFolderId - The folder ID to move sites to
   * @returns {Promise<void>}
   */
  const _handleMoveSitesAndDeleteFolder = async (targetFolderId) => {
    if (state.folderBeingEdited && state.sitesInFolder.length > 0) {
      const siteIds = state.sitesInFolder.map((site) => site.site_design_id);
      const folderIds = targetFolderId === "uncategorized" ? null : Number(targetFolderId);
      await _handleMoveSites({ siteIds, folderIds });
      await _confirmDeleteFolder();
    }
  };

  /**
   * Handles moving selected sites to a specified folder.
   * Processes multiple sites sequentially through the API and provides user feedback.
   *
   * @param {Object} params - The parameters for site movement.
   * @param {number[]} params.siteIds - Array of site IDs to move.
   * @param {number[]} params.folderIds - Array of folder IDs to move sites to.
   * @returns {Promise<void>} Promise that resolves when sites are moved.
   */
  const _handleMoveSites = async ({ siteIds, folderIds }) => {
    const result = await UserService.assignSitesToFolder({ siteIds, folderIds });

    setState((prev) => ({ ...prev, selectedSites: [] }));

    if (!result.data || !result.data.successful) {
      invalidateUserSites();
      return;
    }

    if (result.data.failed.length > 0) {
      invalidateUserSites();
      return;
    }

    invalidateUserSites();
  };

  /**
   * Handles the confirmation of the delete folder action
   * @returns {Promise<void>}
   */
  const _confirmDeleteFolder = async () => {
    if (state.folderBeingEdited) {
      const { value: folderId } = state.folderBeingEdited;

      if (state.selectedFolder === folderId) {
        setState((prev) => ({ ...prev, selectedFolder: "all-site-designs" }));
      }

      await UserService.deleteSiteDesignsFolder({ folderId });

      _closeDeleteFolderModal();
      invalidateSiteDesignsFolders();
      invalidateUserSites();
    }
  };

  /**
   * Handles the selection of a folder. Uncategorized is a virtual placeholder for sites with folder_id: null.
   * @param {Object} option - The selected folder
   * @returns {void}
   */
  const _onFolderSelect = (option) => {
    if (option.label === "Uncategorized") {
      setState((prev) => ({
        ...prev,
        selectedFolder: "uncategorized",
        selectedSites: [],
        selectedSite: null,
        lastSelectedIndex: null,
      }));
    } else {
      setState((prev) => ({
        ...prev,
        selectedFolder: option.value,
        selectedSites: [],
        selectedSite: null,
        lastSelectedIndex: null,
      }));
    }
  };

  const _onRenameFolder = (folder) => {
    _openRenameFolderModal(folder);
  };

  /**
   * handles the deletion of a folder
   * @param {Object} folder - The folder to delete
   * @returns {Promise<void>}
   */
  const _onDeleteFolder = async (folder) => {
    const sitesInFolder = allSites.filter((site) => {
      if (folder.value === "uncategorized") {
        return !site.folder_id || !allFolders.find((f) => f.folderId === site.folder_id);
      }
      const uncategorizedFolder = allFolders.find((f) => f.name === "Uncategorized");
      if (folder.value === uncategorizedFolder?.folderId) {
        return !site.folder_id || site.folder_id === folder.value;
      }
      return site.folder_id === folder.value;
    });

    if (sitesInFolder.length > 0) {
      setState((prev) => ({
        ...prev,
        isMoveSitesModalOpen: true,
        folderBeingEdited: folder,
        sitesInFolder: sitesInFolder,
      }));
    } else {
      _openDeleteFolderModal(folder);
    }
  };

  /**
   * Handles navigation to a specific folder
   * @param {string} folderId - The folder ID to navigate to
   * @returns {void}
   */
  const _handleNavigateToFolder = (folderId) => {
    setState((prev) => {
      const newState = {
        ...prev,
        selectedFolder: folderId,
        selectedSites: [],
        selectedSite: null,
        lastSelectedIndex: null,
      };
      return newState;
    });
  };

  /**
   * Handles deselecting all selected sites (for the deselect button)
   * Clears the selectedSites array, resets selectedSite to null, and clears lastSelectedIndex
   * Also updates the selectedSitesRef to keep it in sync with the state
   * @returns {void}
   */
  const _handleDeselectAll = () => {
    setState((prev) => ({
      ...prev,
      selectedSites: [],
      selectedSite: null,
      lastSelectedIndex: null,
    }));
    selectedSitesRef.current = [];
  };

  const _isFilterDefault = () => !state.siteQuery && state.siteSort === "last_access";

  /**
   * Renders all the modal components
   * @returns {JSX.Element} Modal components
   */
  const _renderModals = () => (
    <>
      <CreateFolderModal
        isOpen={state.isAddFolderModalOpen}
        onClose={_closeAddFolderModal}
        mode={CREATE_MODE}
        existingFolders={allFolders}
      />

      <CreateFolderModal
        isOpen={state.isRenameFolderModalOpen}
        onClose={_closeRenameFolderModal}
        mode={RENAME_MODE}
        folderId={state.folderBeingEdited?.value}
        initialFolderName={state.folderBeingEdited?.label}
        existingFolders={allFolders}
      />

      <DeleteFolderModal
        isOpen={state.isDeleteFolderModalOpen}
        onClose={_closeDeleteFolderModal}
        onConfirm={_confirmDeleteFolder}
        folderName={state.folderBeingEdited?.label}
        isEmpty={state.isFolderEmpty}
      />

      <MoveSitesModal
        isOpen={state.isMoveSitesModalOpen}
        onClose={_closeMoveSitesModal}
        onConfirm={_handleMoveSitesAndDeleteFolder}
        folderName={state.folderBeingEdited?.label}
        sitesInFolder={state.sitesInFolder}
        availableFolders={allFolders}
      />
    </>
  );

  /**
   * Renders the grid content with site tiles or empty state messages
   * @returns {JSX.Element} Grid content
   */
  const _renderGridContent = () => {
    const filteredSites = _filterSites();

    if (filteredSites.length > 0) {
      const siteTiles = [];
      const { selectedSite, selectedSites } = state;

      filteredSites.forEach((site, index) => {
        const { site_design_id: siteId, name: siteName, pub_key: pubKey } = site;
        const domainName = _.get(site, "domain", null);
        let desktopPreview = null;

        if (site.has_preview_imgs === 1 && pubKey !== null) {
          desktopPreview = DesignUtils.getPublishedDesignPreviewImageURL(pubKey, LAYOUT_DESKTOP);
        }

        let menu = null;
        let clickAction = null;
        const mouseLeaveAction = null;
        const isActiveSite = siteId === activeSiteId;
        const isSelected = selectedSites.some((s) => s && s.site_design_id === siteId);

        switch (mode) {
          case USER_SITES_VIEW_MODE_SELECT:
            clickAction = () => _loadSite(site);
            break;
          case USER_SITES_VIEW_MODE_MANAGE:
            clickAction = () => _selectSite(site);
            if (
              selectedSite != null &&
              selectedSite.site_design_id === siteId &&
              selectedSites.length === 1
            ) {
              menu = (
                <UserSiteChooserMenu
                  site={selectedSite}
                  onClose={_closeMenu}
                  openEnabled={!isActiveSite}
                  deleteEnabled={!isActiveSite}
                  setLoadingState={setLoadingState}
                />
              );
            }
            break;
          default:
            break;
        }

        siteTiles.push(
          <LibrarySiteChooserThumbnail
            siteName={siteName}
            domainName={domainName}
            key={siteId}
            siteId={siteId}
            desktopPreview={desktopPreview}
            overlay={menu}
            onClick={clickAction}
            onMouseLeave={mouseLeaveAction}
            highlight={isActiveSite}
            selected={isSelected}
            onToggleSelection={mode === USER_SITES_VIEW_MODE_MANAGE ? _toggleSiteSelection : null}
            index={index}
            site={site}
          />,
        );
      });

      return siteTiles;
    }

    // Empty state
    if (typeof state.selectedFolder === "number" || state.selectedFolder === "uncategorized") {
      return (
        <div className={styles["empty-folder-message"]}>
          <h3>No site designs in this folder</h3>
          <p>This folder is empty. Move sites here to organize them.</p>
        </div>
      );
    }

    return (
      <div className={styles["empty-folder-message"]}>
        <h3>No Results Found</h3>
      </div>
    );
  };

  const _getListView = () => {
    if (isLoading || state.view === USER_SITES_VIEW_CREATING || state.isLoadingSite) {
      return <SubviewProcessing message="Loading" showLogo={false} />;
    }

    if (!isSuccess) {
      return null;
    }

    return (
      <div className="site-chooser-dark">
        <UserSiteChooserHeader
          personalFolders={personalFolders}
          selectedFolder={state.selectedFolder}
          onFolderSelect={_onFolderSelectFromDropdown}
          siteQuery={state.siteQuery}
          onSiteQuery={_onSiteQuery}
          siteSort={state.siteSort}
          onSiteSort={_onSiteSort}
          isFilterDefault={_isFilterDefault()}
          onClearFilters={_clearFilters}
          onCreateNewSite={_createNewSite}
          closeEnabled={closeEnabled}
          onClose={_close}
          selectedSites={state.selectedSites}
          allFolders={allFolders}
          onAddFolder={_openAddFolderModal}
          onMoveSites={_handleMoveSites}
          onDeselectAll={_handleDeselectAll}
          onRenameFolder={_onRenameFolder}
          onDeleteFolder={_onDeleteFolder}
        />

        <div className={styles["site-grid-container"]}>
          <div className="site-chooser-grid">{_renderGridContent()}</div>
        </div>

        {hasUpgradedSite && (
          <div className={styles["site-chooser-legend"]}>
            <UpgradedIcon className={styles["site-chooser-legend-icon"]} />
            <Text>Updated for the latest version of Showit</Text>
          </div>
        )}

        {_renderModals()}
      </div>
    );
  };

  switch (state.view) {
    case USER_SITES_VIEW_LIST:
      return _getListView();
    case USER_SITES_VIEW_CREATING:
      return _getCreatingSiteView();
    default:
      return null;
  }
};

export default UserSiteChooserView;
